/**
 * Wire contract for the Gong reconcile sweep's Admin report
 * (`GET /api/admin/dbos/gong-reconcile/report`,
 * `POST /api/admin/dbos/gong-reconcile/approve-removals`).
 *
 * Shared by the server handlers and the Admin › Workflows panel. The row
 * shape mirrors the `gong_reconcile_report_row` table; `assigneeEmail` stays
 * null on the wire for unassigned rows (the client renders an "Unassigned"
 * bucket).
 */

import { z } from 'zod';

/** Surface id of the sweep workflow; the sweep registers under it and the panel matches on it. */
export const GONG_RECONCILE_SWEEP_WORKFLOW_NAME = 'gong-reconcile-sweep' as const;

export const GongReconcileReportKindSchema = z.enum([
  'would_close',
  'still_enrolled_after_archive',
  'removal_failed',
  'would_hit_ceiling',
]);

export const GongReconcileReportRowWireSchema = z.object({
  signalActionId: z.string(),
  assigneeEmail: z.string().nullable(),
  contactId: z.string().nullable(),
  flowInstanceId: z.string().nullable(),
  decision: z.string(),
  closedSource: z.string().nullable(),
  archiveReason: z.string().nullable(),
  gongStatus: z.string().nullable(),
  kind: GongReconcileReportKindSchema,
});

export const GongReconcileReportResponseSchema = z.object({
  /** The DISPLAYED run: the newest run whose Gong check completed (or the explicitly requested run). */
  run: z.object({ runId: z.string(), observedAt: z.string() }).nullable(),
  /**
   * The newest run when it did NOT complete its Gong check (held, or nothing verified), or the
   * explicitly requested run when that one is incomplete. Null when the displayed run is complete.
   */
  incompleteRun: z.object({ runId: z.string(), observedAt: z.string() }).nullable(),
  wouldHitCeiling: z.boolean(),
  totals: z.object({
    /** Proposed closes, INCLUDING those the ceiling refused. */
    wouldClose: z.number().int().nonnegative(),
    /** How many of the proposed closes the ceiling refused. */
    wouldHitCeiling: z.number().int().nonnegative(),
    stillEnrolledAfterArchive: z.number().int().nonnegative(),
    removalFailed: z.number().int().nonnegative(),
  }),
  byRep: z.array(
    z.object({
      assigneeEmail: z.string().nullable(),
      wouldClose: z.number().int().nonnegative(),
      stillEnrolledAfterArchive: z.number().int().nonnegative(),
      removalFailed: z.number().int().nonnegative(),
      rows: z.array(GongReconcileReportRowWireSchema),
    }),
  ),
  removals: z.object({ available: z.boolean(), reason: z.string().nullable() }),
});

export const ApproveRemovalsRequestSchema = z
  .object({
    runId: z.string().min(1),
    signalActionIds: z.array(z.string().min(1)).min(1),
  })
  .strict();

/**
 * Why a reconcile row ended `unchanged` (or another outcome carrying a note).
 * `report_mode` / `writes_disabled`: a non-keep decision that was deliberately not applied.
 * `close_not_selected`: a close decision skipped because the row is outside a defined
 *   `closeSelection`.
 * `dry_run`: `deps.dryRun` — decided, nothing touched (not even facts).
 */
export const RECONCILE_REASONS = [
  'gong_error',
  'gong_subset_violation',
  'instance_contact_mismatch',
  'ambiguous_contact_answer',
  'no_gong_identity',
  'no_probe_key',
  'bad_signal_id',
  'report_mode',
  'writes_disabled',
  'close_not_selected',
  'dry_run',
  'snooze_needs_date',
  'sf_record_not_found',
  'sf_preread_failed',
  'sf_update_failed',
  'transaction_failed',
] as const;
export type ReconcileReason = (typeof RECONCILE_REASONS)[number];

export const GONG_REMOVAL_OUTCOMES = ['removed', 'already_gone', 'failed', 'skipped'] as const;
export const GONG_REMOVAL_SKIP_REASONS = [
  'no_contact',
  'no_instance',
  'no_flow_id',
  'gong_unavailable',
  'gong_error',
  'contact_mismatch',
] as const;

/** The archive unassign's result (`removed` / `already_gone` / `failed` / `skipped`, plus why). */
export const GongRemovalResultSchema = z.object({
  removal: z.enum(GONG_REMOVAL_OUTCOMES),
  reason: z.enum(GONG_REMOVAL_SKIP_REASONS).optional(),
  flowId: z.string().optional(),
});

/** One approved row's Gong removal outcome for that id. */
export const ApproveRemovalResultSchema = GongRemovalResultSchema.pick({ removal: true, reason: true }).extend({
  signalActionId: z.string(),
});

export const ApproveRemovalsResponseSchema = z.object({
  runId: z.string(),
  /** Same as `approved`; kept because the Admin panel reads it. */
  queued: z.number().int().nonnegative(),
  /** Ids approved (approval recorded and the Gong removal attempted), one per posted id. */
  approved: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
  alreadyGone: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  /** Per-row outcomes, in posted order. */
  results: z.array(ApproveRemovalResultSchema),
});

export type GongReconcileReportKind = z.infer<typeof GongReconcileReportKindSchema>;
export type GongReconcileReportRowWire = z.infer<typeof GongReconcileReportRowWireSchema>;
export type GongReconcileReportResponse = z.infer<typeof GongReconcileReportResponseSchema>;
export type ApproveRemovalsRequest = z.infer<typeof ApproveRemovalsRequestSchema>;
export type ApproveRemovalResult = z.infer<typeof ApproveRemovalResultSchema>;
export type ApproveRemovalsResponse = z.infer<typeof ApproveRemovalsResponseSchema>;
